import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Investment } from '../types/Investment';
import { InvestmentTile } from '../components/InvestmentTile';
import { MAX_SELECTIONS } from '../data/investments';
import { useGameContext } from '../contexts/GameContext';
import { useGameFlow } from '../hooks/useGameFlow';
import { announce, prefersReducedMotion } from '../utils/a11y';
import './InvestmentSelectionScreen.css';

interface InvestmentSelectionScreenProps {
  onConfirm?: (selectedInvestments: Investment[]) => void;
}

export const InvestmentSelectionScreen: React.FC<InvestmentSelectionScreenProps> = ({ onConfirm }) => {
  const { gameState, setSelectedInvestments: setGameInvestments } = useGameContext();
  const { navigateToIncidents } = useGameFlow();
  const [selectedInvestments, setSelectedInvestments] = useState<Investment[]>([]);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const shouldReduceMotion = prefersReducedMotion();
  
  // Use investments from the selected scenario
  const investments = gameState.availableInvestments;

  useEffect(() => {
    // Initialize with any previously selected investments
    if (gameState.selectedInvestments.length > 0) {
      setSelectedInvestments(gameState.selectedInvestments);
    }
  }, [gameState.selectedInvestments]);

  // Scroll to top on mount without interfering with focus
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Announce selection changes
  useEffect(() => {
    const count = selectedInvestments.length;
    const remaining = MAX_SELECTIONS - count;
    
    if (count > 0) {
      const message = count === MAX_SELECTIONS 
        ? `All ${MAX_SELECTIONS} investments selected. You can now confirm your selection.`
        : `${count} of ${MAX_SELECTIONS} investments selected. Select ${remaining} more.`;
      announce(message, 'polite');
    }
  }, [selectedInvestments.length]);

  const handleInvestmentSelect = (investment: Investment) => {
    const isSelected = selectedInvestments.some(inv => inv.id === investment.id);
    
    if (isSelected) {
      // Deselect
      setSelectedInvestments(selectedInvestments.filter(inv => inv.id !== investment.id));
      announce(`${investment.name} deselected`, 'polite');
    } else if (selectedInvestments.length < MAX_SELECTIONS) {
      // Select
      setSelectedInvestments([...selectedInvestments, investment]);
      announce(`${investment.name} selected`, 'polite');
    } else {
      announce(`Cannot select more than ${MAX_SELECTIONS} investments. Please deselect one first.`, 'assertive');
    }
  };



  const canSelectMore = selectedInvestments.length < MAX_SELECTIONS;
  const canConfirm = selectedInvestments.length === MAX_SELECTIONS;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: shouldReduceMotion ? { duration: 0 } : {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: shouldReduceMotion ? { duration: 0 } : {
        duration: 0.5,
      },
    },
  };

  const handleConfirm = () => {
    announce('Confirming investment selections. Proceeding to incident response phase.', 'polite');
    setGameInvestments(selectedInvestments);
    if (onConfirm) {
      onConfirm(selectedInvestments);
    } else {
      navigateToIncidents();
    }
  };

  return (
    <motion.div 
      className="investment-selection-screen"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      role="region"
      aria-label="Investment Selection"
    >
      <motion.header className="screen-header" variants={itemVariants}>
        <h1 ref={headingRef} tabIndex={-1}>Select Your Strategic Investments</h1>
        <p className="header-description" id="investment-instructions">
          Choose exactly {MAX_SELECTIONS} investments to strengthen {gameState.companyName}'s cybersecurity posture. Each investment will affect your ability to respond to incidents.
        </p>
      </motion.header>


      <motion.div className="selection-counter" variants={itemVariants} role="status" aria-live="polite" aria-atomic="true">
        <span className="counter-text">Selected: {selectedInvestments.length}/{MAX_SELECTIONS}</span>
        {!canConfirm && (
          <span className="counter-hint">Select {MAX_SELECTIONS - selectedInvestments.length} more investment{MAX_SELECTIONS - selectedInvestments.length !== 1 ? 's' : ''}</span>
        )}
      </motion.div>

      <motion.div className="investments-grid" variants={containerVariants} role="group" aria-labelledby="investment-instructions">
        {investments.length > 0 ? (
          investments.map(investment => (
            <InvestmentTile
              key={investment.id}
              investment={investment}
              isSelected={selectedInvestments.some(inv => inv.id === investment.id)}
              onSelect={handleInvestmentSelect}
              disabled={!canSelectMore && !selectedInvestments.some(inv => inv.id === investment.id)}
            />
          ))
        ) : (
          <div className="no-investments-message">
            <p>No investments are available for this scenario.</p>
          </div>
        )}
      </motion.div>

      <motion.div className="action-section" variants={itemVariants} role="region" aria-label="Confirm selection">
        <button 
          className="confirm-button"
          type="button"
          onClick={handleConfirm}
          onMouseDown={(e) => {
            e.preventDefault();
            if (canConfirm) {
              handleConfirm();
            }
          }}
          disabled={!canConfirm}
          aria-label={`Confirm selection of ${selectedInvestments.length} investments`}
          aria-describedby="confirm-hint"
        >
          Confirm Investments
        </button>
        {!canConfirm && (
          <p className="action-hint" id="confirm-hint" role="status">
            You must select exactly {MAX_SELECTIONS} investments before proceeding.
          </p>
        )}
      </motion.div>
    </motion.div>
  );
};